import { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import BackButton from "@/components/BackButton";
import { ChevronDown, HelpCircle } from "lucide-react";

const FAQS = [
  { q: "What is NextScene?", a: "NextScene is a talent discovery platform to showcase your skills — acting, singing, dance and more. It is a discovery platform, not a job board — we do not guarantee any auditions, selection, or employment." },
  { q: "Who can join?", a: "Anyone. Users under 13 must have a parent or guardian manage the account. Users aged 13–17 need parent or guardian permission. Users 18+ join directly." },
  { q: "Is it free?", a: "Yes — core features (profile, uploads, discovery, casting inquiries) are free forever. Premium features (deeper analytics, priority listing, verification) unlock with Creator Pass." },
  { q: "What kind of videos can I upload?", a: "Original performances only — acting, singing, dance, comedy, poetry, voice work, choreography, modeling and more. Follow our community guidelines. Do not upload content you don't own or don't have permission to share." },
  { q: "Does participating guarantee selection?", a: "No. NextScene is a discovery platform, not a talent agency. We do not guarantee auditions, selection, employment, or any specific outcome from any casting call or Creator Pass subscription." },
  { q: "What is Creator Pass?", a: "Creator Pass unlocks premium features — deeper analytics, priority discovery placement, higher push notification limits, verified badge eligibility, and more. Payments are non-refundable except where required by law." },
  { q: "How do I report a user?", a: "Tap the Report button (flag icon) on any profile or video and choose a reason — Copyright, Spam, Abusive, Nudity, or Child Safety. Child safety reports auto-hide the content immediately for our team's review." },
  { q: "How do I delete my account?", a: "Open the app → Settings → Delete Account. Type your username to confirm. This hides your profile and content immediately. Recovery is manual within 30 days via support." },
  { q: "Payment failure help?", a: "Wait 5–10 minutes for auto-retry. If money was deducted but Creator Pass didn't activate, contact support with your payment reference ID (visible under Settings → Payment History) and we'll resolve within 3 business days." },
  { q: "How do I contact support?", a: "Open Settings → Support and use the in-app Report Bug / Help form. You can also email us at support@nextscene.in. Our team typically replies within 24 hours." },
];

export default function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#0A0605] text-white">
      <header className="max-w-3xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/"><Logo size="md"/></Link>
        <BackButton fallback="/" label="Back"/>
      </header>
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="flex items-center gap-2 mb-2">
          <HelpCircle size={20} className="text-[#F5C462]"/>
          <p className="text-xs uppercase tracking-[0.2em] text-[#F5C462]">Frequently asked</p>
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white">Questions we get a lot.</h1>
        <p className="text-sm text-zinc-400 mt-2">Ten answers that cover 90% of what new creators ask us.</p>

        <div className="mt-8 space-y-2">
          {FAQS.map((f, i) => (
            <div key={i} data-testid={`faq-item-${i}`} className="rounded-2xl border border-zinc-800 bg-[#180D0D] overflow-hidden">
              <button
                data-testid={`faq-toggle-${i}`}
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-white/[0.02] transition-colors"
              >
                <span className="text-sm font-semibold text-white pr-2">{f.q}</span>
                <ChevronDown size={16} className={`text-[#F5C462] shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}/>
              </button>
              {open === i && (
                <div data-testid={`faq-answer-${i}`} className="px-4 pb-4 text-sm text-zinc-300 leading-relaxed border-t border-zinc-800/50 pt-3">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-xs text-zinc-500 mt-10 text-center">
          Still have a question? <Link to="/app/settings" className="text-[#F5C462] hover:text-white underline">Contact support</Link>
        </p>
      </main>
    </div>
  );
}
